'use strict';

import Minimap3DLayout from './Minimap3DLayout.js';
import Minimap3DMobileLayout from './Minimap3DMobileLayout.js';
import { SheetSyncEvents } from "../SheetSync/SheetSyncConstants.js";
import { mapIcon } from './resources/Icons.js';

const av = Autodesk.Viewing;
const avp = av.Private;
const avu = av.UI;
const Empty2DModelLoader = avp.Empty2DModelLoader;

const namespace = AutodeskNamespace('Autodesk.AEC');
const myExtensionName = 'Autodesk.AEC.Minimap3DExtension';

const BIMWALK_EXT = 'Autodesk.BimWalk';
const AVATAR_EXT = 'Autodesk.AEC.AvatarExtension';
const TOP_VIEW_EXT = 'Autodesk.AEC.TopViewRendererExtension';
const LEVEL_EXT = "Autodesk.AEC.LevelsExtension";
const SHEET_SYNC_EXT = "Autodesk.AEC.SheetSyncExtension";

// ENABLE_PROP is connected to preferences, to enable/disable the minimap from the settings panel.
const ENABLE_PROP = 'miniMap3D';
// EXPANDED_PROP is only used when "enableAlwaysOn" option is given: expand/minimize minimap from toolbar.
const EXPANDED_PROP = 'minimap3dExpanded';

const MINIMAP_TOOLBAR_ID = 'minimap-toolbar';

const MinimapLayouts = {
    Minimap3DLayout,
    Minimap3DMobileLayout,
};

const Events = {
    VISIBILITY_CHANGED: 'visibility_changed',
    LARGE_MODE_CHANGED: 'large_mode_changed',
    EXPANDED_CHANGED: 'expanded_changed',
};

export default class Minimap3DExtension extends av.Extension {
    constructor(viewer, options = {}) {
        super(viewer, options);

        av.EventDispatcher.prototype.apply(this);

        this.mapViewerHighlightEnabled = options.mapViewerHighlightEnabled || false; // By default, mapViewer highlight (hover) is off.
        this.mapViewerSelectionEnabled = options.mapViewerSelectionEnabled || false; // By default, mapViewer 2D object selection is off.
        this.disableAnimations = options.disableAnimations ?? false;

        this.onToolChange = this.onToolChange.bind(this);
        this.update = this.update.bind(this);
        this.onModelAdded = this.onModelAdded.bind(this);
        this.changeMinimapVisibility = this.changeMinimapVisibility.bind(this);
        this.changeMinimapExpanded = this.changeMinimapExpanded.bind(this);

        // Resize animation duration in seconds.
        this.resizeDuration = this.disableAnimations ? 0 : 0.3;
    }

    async load() {
        // Bear in mind that in case the application wants to load the LevelsExtension with specific options argument,
        // It should load the LevelsExtension *before* loading Minimap3D extension.
        this.levelExt = await this.viewer.loadExtension(LEVEL_EXT);

        this.loaded = true;

        this.viewer.getPreferences().add(ENABLE_PROP, true);
        this.viewer.getPreferences().add(EXPANDED_PROP, false, 'shared-storage');

        if (!this.options.enableAlwaysOn && !this.configureSettings()) {
            if (!this.updateSettingsPanel) {
                //We need to connect this to an event that happens after the settings panel has been
                //create by the load sequence.
                this.updateSettingsPanel = this.configureSettings.bind(this);
                this.viewer.addEventListener(av.SETTINGS_PANEL_CREATED_EVENT, this.updateSettingsPanel);
            }
        }

        this.viewer.addEventListener(av.TOOL_CHANGE_EVENT, this.onToolChange);
        this.viewer.addEventListener(av.MODEL_ADDED_EVENT, this.onModelAdded);

        if (this.levelExt?.floorSelector) {
            this.levelExt.floorSelector.addEventListener(Autodesk.AEC.FloorSelector.FLOOR_DATA_CHANGED, this.update);
        }

        this.activate();

        return true;
    }

    unload() {
        this.loaded = false;

        this.deactivate();

        if (this.updateSettingsPanel) {
            this.viewer.removeEventListener(av.SETTINGS_PANEL_CREATED_EVENT, this.updateSettingsPanel);
            this.updateSettingsPanel = null;
        }

        this.viewer.removeEventListener(av.TOOL_CHANGE_EVENT, this.update);
        this.viewer.removeEventListener(av.MODEL_ADDED_EVENT, this.onModelAdded);

        this.levelExt = null;

        // Make sure that we don't leak models
        return true;
    }

    onModelAdded() {
        // In case the viewer is now on 2D mode, destroy the mapViewer, since it's not relevant anymore.
        // By destroying mapViewer, it will unload all of it's minimap related extensions as well.

        this.viewer.impl.is2d ? this.deactivate() : this.activate();
    }

    onToolChange(event) {
        // If enableAlwaysOn is given minimap should be expanded when BIM-Walk is activated.
        const bimWalkTool = this.viewer.getExtension(BIMWALK_EXT)?.tool;
        if (this.options.enableAlwaysOn && event.toolName === bimWalkTool?.getName() && event.active) {
            this.changeMinimapExpanded(true);
        }

        this.update();
    }

    onToolbarCreated() {
        if (!this.options.enableAlwaysOn)
            return;

        this.minimapButton = new avu.Button("toolbar-levelsTool");
        this.minimapButton.setToolTip('Minimap');
        this.minimapButton.icon.innerHTML = mapIcon;
        this.minimapButton.onClick = () => {
            this.changeMinimapExpanded(!this.viewer.getPreferences().get(EXPANDED_PROP));
        };

        this.toolbarGroup = new avu.ControlGroup(MINIMAP_TOOLBAR_ID);
        this.toolbarGroup.setGlobalManager(this.viewer.globalManager);
        this.toolbarGroup.setVisible(true);
        this.toolbarGroup.addControl(this.minimapButton);

        let toolbar = this.viewer.getToolbar();
        toolbar.addControl(this.toolbarGroup);
    }

    activate() {
        if (this.active) {
            return;
        }

        this.update();

        this.active = true;
    }

    deactivate() {
        if (!this.active) {
            return;
        }

        this.active = false;

        if (this.mapViewer) {
          this.mapViewer.finish();
          this.sheetSyncExtension = null;
          this.mapViewer = null;
        }

        if (this.layout) {
            this.layout.destroy && this.layout.destroy();
            this.layout = null;
        }

        if (this.levelExt?.floorSelector) {
            this.levelExt.floorSelector.removeEventListener(Autodesk.AEC.FloorSelector.FLOOR_DATA_CHANGED, this.update);
        }
    }


    // Only if there is a valid floorData (based on AecModelData), the minimap can work.
    // If enableWithoutAecModelData is given, the minimap can still work, but without level-based sectioning - which means that we'll always see the roof.
    hasAecData() {
        return this.options.enableWithoutAecModelData || this.levelExt?.floorSelector?.floorData?.length > 0;
    }

    // Check if minimap is enabled:
    // - enableAlwaysOn option is given, then check for visible property.
    // - Otherwise: check enabled in viewer settings & BIM-Walk is active.
    isEnabled() {
        // If enableAlwaysOn is given...
        //   1. the minimap will always be enabled (even outside BIM-Walk).
        //   2. the minimap can be toggled using 'miniMap3D_visibility' preference.
        if (this.options.enableAlwaysOn) {
            const visible = this.viewer.getPreferences().get(EXPANDED_PROP);
            return visible && (this.hasAecData() || this.options.minimapDebugMode);
        }

        // Check if minimap pref is setting panel is enabled.
        const prefEnabled = this.viewer.getPreferences() && this.viewer.getPreferences().get(ENABLE_PROP);

        // Check if bim walk is active.
        const bimWalkExt = this.viewer.getExtension(BIMWALK_EXT);
        const bimWalkEnabled = bimWalkExt?.tool?.active;

        return prefEnabled && ((bimWalkEnabled && this.hasAecData()) || this.options.minimapDebugMode);
    }

    changeMinimapVisibility(visible) {
        if (this.viewer.getPreferences()) {
            this.viewer.getPreferences().set(ENABLE_PROP, visible);
        }

        this.fireEvent({ type: Events.VISIBILITY_CHANGED, visible });

        avp.analytics.track('viewer.3d_minimap', {
            action: visible ? 'Show' : 'Hide'
        });

        this.update();
    }

    changeMinimapExpanded(expanded) {
        if (!this.options.enableAlwaysOn) {
            return;
        }

        if (this.viewer.getPreferences()) {
            this.viewer.getPreferences().set(EXPANDED_PROP, expanded);
        }

        this.fireEvent({ type: Events.EXPANDED_CHANGED, expanded });

        avp.analytics.track('viewer.3d_minimap', {
            action: expanded ? 'Expanded' : 'Minimized'
        });

        this.update();
    }

    initLayoutIfNeeded() {
        if (this.layout) {
            return;
        }

        let LayoutClass;

        if (!this.options.LayoutClass) {
            LayoutClass = MinimapLayouts.Minimap3DLayout;
        } else if (typeof this.options.LayoutClass === 'string') {
            LayoutClass = MinimapLayouts[this.options.LayoutClass];

            if (!LayoutClass) {
                console.warn('Invalid LayoutClass. Fallback to default layout.');
                LayoutClass = MinimapLayouts.Minimap3DLayout;
            }
        } else {
            LayoutClass = this.options.LayoutClass;
        }

        this.layout = new LayoutClass(this);
    }

    initMapViewerIfNeeded() {
        if (this.mapViewer) {
            return;
        }

        const container = this.layout.getMapViewerContainer();

        if (this.options.getMapViewer) {
            // Get mapViewer using an external callback.
            // Important! if you create a viewer of your own, make sure to initialize it with { webglInitParams: { alpha: true }.
            // Otherwise, you won't be able to see the 3D top view.
            this.mapViewer = this.options.getMapViewer(container);

        } else {
            // Generate a new mapViewer.
            const ViewerClass = this.options.MapViewerClass || Autodesk.Viewing.Viewer3D;

            this.mapViewer = new ViewerClass(container, { childViewer: true });

            // Map viewer has to have a transparent background in order to support topViewRenderer on the back. This is why `alpha:true` is mandatory.
            this.mapViewer.start(undefined, undefined, undefined, undefined, { webglInitParams: { alpha: true }});
        }

        // Load SheetSync, Avatar & topViewRenderer extensions.
        this.mapViewer.config.extensions = this.mapViewer.config.extensions || [];

        this.mapViewer.config.sheetSyncOptions = {
            targetViewer: this.viewer,
            cropToViewport: true,
            hideSheetWhenNotFocused: true,
            autoSwitchSheet: true,
            hidePaper: true,
            useCustomModel: this.options.useCustomModel,
            showSheet: this.options.showSheet,
            loadDocumentNode: this.options.loadDocumentNode,
            unloadModel: this.options.unloadModel,
            minimapDebugMode: this.options.minimapDebugMode,
            useOnlyAecModelDataViewports: this.options.useOnlyAecModelDataViewports,
            disableAnimations: this.disableAnimations,
        };

        this.mapViewer.config.avatarOptions = {
            trackUsage: this.options.trackUsage,
            autoFocus: this.options.autoFocus ?? !this.options.minimapDebugMode,
            disableAnimations: this.disableAnimations,
        };

        this.mapViewer.config.topViewRendererOptions = {
            minimapDebugMode: this.options.minimapDebugMode,
            disableAnimations: this.disableAnimations,
        };

        if (this.mapViewer.config.extensions.indexOf(AVATAR_EXT) === -1) {
            this.mapViewer.config.extensions.push(AVATAR_EXT);
        }

        if (this.mapViewer.config.extensions.indexOf(TOP_VIEW_EXT) === -1) {
            this.mapViewer.config.extensions.push(TOP_VIEW_EXT);
        }

        this.mapViewer.disableHighlight(!this.mapViewerHighlightEnabled);
        this.mapViewer.disableSelection(!this.mapViewerSelectionEnabled);

        this.mapViewer.getExtensionAsync(SHEET_SYNC_EXT).then((sheetSyncExtension) => {
            this.sheetSyncExtension = sheetSyncExtension;
        });

        this.mapViewer.addEventListener(SheetSyncEvents.SHEET_VISIBILITY_CHANGED_EVENT, this.update);

        // Load an empty model to initialize extensions and enable camera.
        this.mapViewer.loadModel("Dummy", { fileLoader: Empty2DModelLoader, headlessViewer: true });

        this.mapViewer.impl.setFrameTimePriority(0.01);
        this.mapViewer.getExtensionAsync(TOP_VIEW_EXT).then( (topViewExt) => {
            topViewExt.topViewViewer.impl.setFrameTimePriority(0.1);
        });
    }

    update() {
        if (this.isEnabled()) {
            this.initLayoutIfNeeded();
            this.initMapViewerIfNeeded();
        }

        if (this.layout && this.layout.update) {
            this.layout.update();
        }

        if (this.options.enableAlwaysOn) {
            this.updateMinimapButton();
        }
    }

    updateMinimapButton() {
        if (!this.minimapButton) {
            return;
        }

        const buttonState =
            !this.hasAecData() ? avu.Button.State.DISABLED :
            this.isEnabled() ? avu.Button.State.ACTIVE : avu.Button.State.INACTIVE;

        this.minimapButton.setState(buttonState);
    }

    configureSettings() {

        //Is it a GUI viewer or not?
        if (!this.viewer.getSettingsPanel)
            return false;

        let panel = this.viewer.getSettingsPanel();

        if (!panel || !panel.is3dMode)
            return false;

        let navTab = av.Extensions.ViewerSettingTab.Navigation;
        panel.addLabel(navTab, "3D Mini-map");

        // add toggle to the hypermodeling tab of the viewer settings
        if (!panel.showMinimapChkBoxId) {
            panel.showMinimapChkBoxId = panel.addCheckbox(
                navTab,
                "Show 3D mini-map",
                "Toggles availability of the 3D mini-map when using first person mode",
                true,
                this.changeMinimapVisibility,
                ENABLE_PROP
            );
        }

        return true;
    }

    // Toggle between auto-generated map image and sheets. Note that sheets may not be available for a level.
    // In this case, we fall back to the default map.
    setSheetMapEnabled(enabled) {
        if (this.sheetSyncExtension) {
            this.sheetSyncExtension.setShowSheet(enabled);
            this.update();
        }
    }

    getSheetMapEnabled() {
        return this.sheetSyncExtension?.getShowSheet();
    }

    setSideBySideEnabled(enabled) {
        if (this.layout?.setSideBySideEnabled) {
            this.layout.setSideBySideEnabled(enabled);

            avp.analytics.track('viewer.3d_minimap', {
                action: enabled ? 'LargeView' : 'SmallView'
            });
        }
    }

    setSideBySideSplit(value) {
        if (this.layout?.setSideBySideSplit) {
            this.layout.setSideBySideSplit(value);
        }
    }

    isSheetVisible() {
        return this.sheetSyncExtension?.isSheetVisible();
    }

    // Locking avatar rotation (Look Around) navigation
    setLockDragDirection(isLocked) {
        this.mapViewer.getExtension(AVATAR_EXT)?.setDragDirectionLocked(isLocked);
    }

    async waitForRotationDone() {
        const topViewRendererExt = this.mapViewer.getExtension(TOP_VIEW_EXT);
        await topViewRendererExt.waitForRotationDone();
    }

    async hideAvatarAndWait() {
        const avatarExt = this.mapViewer.getExtension(AVATAR_EXT);
        await avatarExt.hide();
    }

    async expandSheetMap() {
        // First, request sheetNode in the background (it might take time only if it wasn't loaded before).
        const sheetNodePromise = this.sheetSyncExtension.sheetMap.getSheetNode();

        this.sheetSyncExtension.expandCropBounds(this.resizeDuration);
        this.sheetSyncExtension.changePaperVisibility(true);
        await this.layout.expandSheetMap(this.resizeDuration);
        await this.waitForRotationDone();
        this.sheetSyncExtension.setSheetOpaque(this.resizeDuration);
        await this.hideAvatarAndWait();
        const viewerState = this.mapViewer.getState();

        const sheetNode = await sheetNodePromise;

        const file_2d_extension = sheetNode.getInputFileType();
        // In case of more than one loaded model, send "Aggregate".
        const file_3d_extension = this.mapViewer.getVisibleModels().length === 1 ? this.mapViewer.model.getDocumentNode().getInputFileType() : 'Aggregate';

        avp.analytics.track('viewer.3d_minimap.open_sheet', {
            file_2d_extension,
            file_3d_extension,
        });

        // CustomHash should be used when loading the expanded sheet into another Leech Viewer.
        // If not supplied, the sheet will be reloaded from scratch instead of using the shared model cache.
        await this.options.onExpand(sheetNode, this.sheetSyncExtension.getCustomHash());
        this.viewer.restoreState(viewerState, undefined, true);
    }
}

namespace.Minimap3DExtension = Minimap3DExtension;
namespace.Minimap3DExtension.Events = Events;

Autodesk.Viewing.theExtensionManager.registerExtension(myExtensionName, Minimap3DExtension);
